import React, {useState, useMemo, useEffect, useRef, useCallback} from 'react';
import {useTable, useRowSelect, useSortBy} from 'react-table';
import {useTranslation} from 'react-i18next';
import {formatLocalizedDate} from '../../utils/dateUtils';
import {
  formatGiftApprovalDetail,
  formatRewardApprovalDetail,
} from '../../utils/approvalDetail';
import {getAbsoluteImageUrl} from '../../utils/imageUtils';
import {ContentWrapper, Button} from '@components';
import {X} from 'react-feather';
import {ReactComponent as SearchIcon} from '@images/search.svg';
import {
  getReward,
  getPurchases,
  Acceptapi,
  getRewardNewApi,
  getGiftData,
  changeRewardStatus,
  changeGiftStatus,
  ProductPurchaseNew,
  bulkApprove,
  bulkApprovePurchase,
  bulkApproveGift,
  showBulkPurchaseResultToasts,
  showPurchaseStatusChangeSuccessToast,
} from '@apis';
import {ReactComponent as Gift} from '@images/gift.svg';
import {ReactComponent as User} from '@images/user.svg';
import {ReactComponent as Basket} from '@images/basket.svg';
import {ReactComponent as Star} from '@images/star.svg';
import debby from '@images/debby.png';
import {useMutation} from 'react-query';
import {data} from 'autoprefixer';
import toast from 'react-hot-toast';

const Approve = () => {
  const {t, i18n} = useTranslation();

  const [acceptearn, setacceptearnData] = useState([]);
  const [rejectearn, setrejectearnData] = useState([]);
  const [acceptpurchase, setacceptpurchaseData] = useState([]);
  const [rejectpurchase, setrejectpurchaseData] = useState([]);
  const [RewardData, setRewardData] = useState([]);
  const [GiftData, setGiftData] = useState([]);
  const [PurchaseData, setPurchaseData] = useState([]);
  const [columns, setColumn] = useState('1');
  const [selectedRow, setSelectedRow] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rewardNextPage, setRewardNextPage] = useState('');
  const [rewardpreviousPage, setRewardPreviousPage] = useState('');
  const [giftNextPage, setGiftNextPage] = useState('');
  const [giftpreviousPage, setGiftPreviousPage] = useState('');
  const [purchaseNextPage, setPurchaseNextPage] = useState('');
  const [purchasepreviousPage, setPurchasePreviousPage] = useState('');
  const [loading, setLoading] = useState(false);
  const [rewardCount, setRewardCount] = useState(0);
  const [giftCount, setGiftCount] = useState(0);
  const [purchaseCount, setPurchaseCount] = useState(0);
  const [processingPurchases, setProcessingPurchases] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const getuseData = async val => {
    setColumn(val);
    setSelectedRow([]);
  };

  const loadRequestId = useRef(0);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
      setSelectedRow([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const applyRewardList = useCallback(rewardData => {
    if (rewardData?.results?.length > 0) {
      setRewardData(rewardData.results);
    } else {
      setRewardData([]);
    }
    setRewardCount(rewardData?.count ?? 0);
    setRewardNextPage(rewardData?.next ?? null);
    setRewardPreviousPage(rewardData?.previous ?? null);
  }, []);

  const applyGiftList = useCallback(giftData => {
    if (giftData?.results?.length > 0) {
      setGiftData(giftData.results);
    } else {
      setGiftData([]);
    }
    setGiftCount(giftData?.count ?? 0);
    setGiftNextPage(giftData?.next ?? null);
    setGiftPreviousPage(giftData?.previous ?? null);
  }, []);

  const applyPurchaseList = useCallback(purchasedData => {
    if (purchasedData?.results?.length > 0) {
      setPurchaseData(purchasedData.results);
    } else {
      setPurchaseData([]);
    }
    setPurchaseCount(purchasedData?.count ?? 0);
    setPurchaseNextPage(purchasedData?.next ?? null);
    setPurchasePreviousPage(purchasedData?.previous ?? null);
  }, []);

  const loadApprovalLists = useCallback(
    async (tab, page = currentPage, q = debouncedSearch) => {
      const requestId = ++loadRequestId.current;
      setLoading(true);

      const trimmedQuery = String(q ?? '').trim();
      const isSearching = Boolean(trimmedQuery);
      const listParams = (activeTab, activePage) => ({
        page: activeTab === tab ? activePage : 1,
        page_size: rowsPerPage,
        q: trimmedQuery,
      });
      const countParams = {
        page: 1,
        page_size: 1,
        q: trimmedQuery,
      };

      const isCurrentRequest = () => requestId === loadRequestId.current;

      try {
        if (isSearching) {
          const [rewardData, giftData, purchasedData] = await Promise.all([
            getRewardNewApi(listParams('1', page), 'pending'),
            getGiftData(listParams('2', page), 'pending'),
            getPurchases(listParams('3', page), 'pending'),
          ]);
          if (!isCurrentRequest()) {
            return;
          }
          applyRewardList(rewardData);
          applyGiftList(giftData);
          applyPurchaseList(purchasedData);
          return;
        }

        const inactiveFetches = [];
        if (tab !== '1') {
          inactiveFetches.push(
            getRewardNewApi(countParams, 'pending').then(rewardData => {
              if (isCurrentRequest()) {
                setRewardCount(rewardData?.count ?? 0);
              }
            }),
          );
        }
        if (tab !== '2') {
          inactiveFetches.push(
            getGiftData(countParams, 'pending').then(giftData => {
              if (isCurrentRequest()) {
                setGiftCount(giftData?.count ?? 0);
              }
            }),
          );
        }
        if (tab !== '3') {
          inactiveFetches.push(
            getPurchases(countParams, 'pending').then(purchasedData => {
              if (isCurrentRequest()) {
                setPurchaseCount(purchasedData?.count ?? 0);
              }
            }),
          );
        }

        const activeParams = listParams(tab, page);
        let activeData;
        if (tab === '1') {
          activeData = await Promise.all([
            getRewardNewApi(activeParams, 'pending'),
            ...inactiveFetches,
          ]).then(([rewardData]) => rewardData);
          if (!isCurrentRequest()) {
            return;
          }
          applyRewardList(activeData);
        } else if (tab === '2') {
          activeData = await Promise.all([
            getGiftData(activeParams, 'pending'),
            ...inactiveFetches,
          ]).then(([giftData]) => giftData);
          if (!isCurrentRequest()) {
            return;
          }
          applyGiftList(activeData);
        } else {
          activeData = await Promise.all([
            getPurchases(activeParams, 'pending'),
            ...inactiveFetches,
          ]).then(([purchasedData]) => purchasedData);
          if (!isCurrentRequest()) {
            return;
          }
          applyPurchaseList(activeData);
        }
      } finally {
        if (isCurrentRequest()) {
          setLoading(false);
        }
      }
    },
    [
      applyGiftList,
      applyPurchaseList,
      applyRewardList,
      currentPage,
      debouncedSearch,
      rowsPerPage,
    ],
  );

  const RewardAccept = useMutation(params => Acceptapi(params));

  const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
      const defaultRef = React.useRef();
      const resolvedRef = ref || defaultRef;

      React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);

      return (
        <>
          <input type="checkbox" ref={resolvedRef} {...rest} />
        </>
      );
    },
  );

  const Table = ({
    columns,
    data,
    onSelectedRowChange,
    loading,
    selectedRow,
  }) => {
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      selectedFlatRows,
      state: {selectedRowIds},
    } = useTable(
      {
        columns,
        data,
        onRowSelectionChange: row => {},
      },
      useSortBy,
      useRowSelect,
      hooks => {
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            Header: ({getToggleAllRowsSelectedProps}) => (
              <div>
                <IndeterminateCheckbox
                  {...getToggleAllRowsSelectedProps()}
                  onClick={e => {
                    onSelectedRowChange(e, 1, data, 'all');
                  }}
                  checked={
                    data.length === selectedRow.length && data.length !== 0
                  }
                />
              </div>
            ),
            Cell: ({row}) => (
              <div>
                <IndeterminateCheckbox
                  {...row.getToggleRowSelectedProps()}
                  checked={selectedRow.includes(row.original.id)}
                  onClick={e => {
                    onSelectedRowChange(e, row.original.id, data);
                  }}
                  className="cursor-pointer"
                />
              </div>
            ),
          },
          ...columns,
        ]);
      },
    );

    return (
      <table {...getTableProps()} className="min-w-full">
        <thead className="bg-tableHeaderBg p-4">
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="text-left text-menu font-normal uppercase text-sm px-4 py-2">
                  {column.render('Header')}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td
                colSpan={headerGroups[0]?.headers.length || 1}
                className="p-4 text-center text-sm">
                {t('common.loading')}
              </td>
            </tr>
          ) : (
            rows.map(row => {
              prepareRow(row);
              return (
                <tr key={row.id} {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td
                      key={cell.column.id}
                      {...cell.getCellProps()}
                      className="p-4 whitespace-nowrap text-sm">
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    loadApprovalLists(columns, currentPage, debouncedSearch);
  }, [columns, currentPage, refresh, debouncedSearch, loadApprovalLists]);

  //Accept Reward Data
  const acceptEarn = async id => {
    var paramdata = {
      status: 'accepted',
    };
    console.log(id.original.id);

    if (id.original.status) {
      let res = await changeRewardStatus(id.original.id, paramdata);
    } else {
      paramdata = {
        accepted: true,
      };
    }
    setCurrentPage(1);
    loadApprovalLists(columns, 1, debouncedSearch);
  };

  //Reject Reward Data
  const rejectEarn = async id => {
    var paramdata = {
      status: 'rejected',
    };
    if (id.original.status) {
      let res = await changeRewardStatus(id.original.id, paramdata);
    } else {
      paramdata = {
        accepted: false,
      };
    }
    setCurrentPage(1);
    loadApprovalLists(columns, 1, debouncedSearch);
  };

  //Accept Gift Data
  const acceptGift = async id => {
    var paramdata = {
      status: 'accepted',
    };

    if (id.original.status) {
      let res = await changeGiftStatus(id.original.id, paramdata);
    } else {
      paramdata = {
        accepted: true,
      };
    }
    setCurrentPage(1);
    loadApprovalLists(columns, 1, debouncedSearch);
  };

  //Reject Gift Data
  const rejectGift = async id => {
    var paramdata = {
      status: 'rejected',
    };

    if (id.original.status) {
      let res = await changeGiftStatus(id.original.id, paramdata);
    } else {
      paramdata = {
        accepted: false,
      };
    }
    setCurrentPage(1);
    loadApprovalLists(columns, 1, debouncedSearch);
  };

  const setPurchaseProcessing = (purchaseId, isProcessing) => {
    setProcessingPurchases(prev => ({
      ...prev,
      [purchaseId]: isProcessing,
    }));
  };

  //Accept Purchase Data
  const acceptPurchase = async row => {
    const purchaseId = row.original.id;

    if (processingPurchases[purchaseId]) {
      return;
    }

    if (
      !row.original.points_charged_on_request &&
      row.original.points_paid > row.original.user_current_points
    ) {
      toast.error(t('approve.insufficientPoints'));
      return;
    }

    setPurchaseProcessing(purchaseId, true);
    try {
      await ProductPurchaseNew(purchaseId, {accepted: true});
      showPurchaseStatusChangeSuccessToast(true);
      setCurrentPage(1);
      loadApprovalLists(columns, 1, debouncedSearch);
    } catch (error) {
      toast.error(
        typeof error === 'string'
          ? error
          : t('approve.errorApprovingItems'),
      );
    } finally {
      setPurchaseProcessing(purchaseId, false);
    }
  };

  //Reject Purchase Data
  const rejectPurchase = async row => {
    const purchaseId = row.original.id;

    if (processingPurchases[purchaseId]) {
      return;
    }

    setPurchaseProcessing(purchaseId, true);
    try {
      await ProductPurchaseNew(purchaseId, {accepted: false});
      showPurchaseStatusChangeSuccessToast(false);
      setCurrentPage(1);
      loadApprovalLists(columns, 1, debouncedSearch);
    } catch (error) {
      toast.error(
        typeof error === 'string'
          ? error
          : t('approve.errorRejectingItems'),
      );
    } finally {
      setPurchaseProcessing(purchaseId, false);
    }
  };

  const Acceptslected = async () => {
    // Check if any items are selected
    if (!selectedRow || selectedRow.length === 0) {
      toast.error(
        t(
          'approve.noItemsSelected',
          'Please select at least one item to approve',
        ),
      );
      return;
    }

    try {
      if (columns === '1') {
        const newBonus = await bulkApprove({
          reward_ids: selectedRow,
          status: 'accepted',
        });
        setSelectedRow([]);
        setCurrentPage(1);
        loadApprovalLists(columns, 1, debouncedSearch);
        toast.success(
          t(
            'approve.selectedItemsApproved',
            `${selectedRow.length} reward(s) approved successfully`,
          ),
        );
      } else if (columns === '2') {
        const newBonus = await bulkApproveGift({
          gift_ids: selectedRow,
          status: 'accepted',
        });
        setSelectedRow([]);
        setCurrentPage(1);
        loadApprovalLists(columns, 1, debouncedSearch);
        toast.success(
          t(
            'approve.selectedItemsApproved',
            `${selectedRow.length} gift(s) approved successfully`,
          ),
        );
      } else if (columns === '3') {
        const result = await bulkApprovePurchase({
          purchase_ids: selectedRow,
          accepted: true,
        });
        setSelectedRow([]);
        setCurrentPage(1);
        loadApprovalLists(columns, 1, debouncedSearch);
        showBulkPurchaseResultToasts(result, true);
      }
    } catch (error) {
      console.error('Error approving selected items:', error);
      toast.error(
        t(
          'approve.errorApprovingItems',
          'Error approving selected items. Please try again.',
        ),
      );
    }
  };

  const Rejectslected = async () => {
    // Check if any items are selected
    if (!selectedRow || selectedRow.length === 0) {
      toast.error(
        t(
          'approve.noItemsSelected',
          'Please select at least one item to reject',
        ),
      );
      return;
    }

    try {
      if (columns === '1') {
        const newBonus = await bulkApprove({
          reward_ids: selectedRow,
          status: 'rejected',
        });
        setSelectedRow([]);
        setCurrentPage(1);
        loadApprovalLists(columns, 1, debouncedSearch);
        toast.success(
          t(
            'approve.selectedItemsRejected',
            `${selectedRow.length} reward(s) rejected successfully`,
          ),
        );
      } else if (columns === '2') {
        const newBonus = await bulkApproveGift({
          gift_ids: selectedRow,
          status: 'rejected',
        });
        setSelectedRow([]);
        setCurrentPage(1);
        loadApprovalLists(columns, 1, debouncedSearch);
        toast.success(
          t(
            'approve.selectedItemsRejected',
            `${selectedRow.length} gift(s) rejected successfully`,
          ),
        );
      } else if (columns === '3') {
        const result = await bulkApprovePurchase({
          purchase_ids: selectedRow,
          accepted: false,
        });
        setSelectedRow([]);
        setCurrentPage(1);
        loadApprovalLists(columns, 1, debouncedSearch);
        showBulkPurchaseResultToasts(result, false);
      }
    } catch (error) {
      console.error('Error rejecting selected items:', error);
      toast.error(
        t(
          'approve.errorRejectingItems',
          'Error rejecting selected items. Please try again.',
        ),
      );
    }
  };

  const fetchData = async () => {
    // const RewardedData = await getReward();

    const RewardedData = await getRewardNewApi();

    const PurchasedData = await getPurchases();

    var acceptreward_earned = [];
    var rejectreward_earned = [];
    var acceptreward_purchased = [];
    var rejectreward_purchased = [];
    if (RewardedData.length > 0) {
      for (var i = 0; i < RewardedData.length; i++) {
        RewardedData[i].user.id = RewardedData[i].id;
        if (RewardedData[i].accepted == false) {
          rejectreward_earned.push(RewardedData.results[i]);
        } else if (RewardedData.results[i].accepted == true) {
          acceptreward_earned.push(RewardedData.results[i]);
        }
      }
    }
    if (PurchasedData.results.length > 0) {
      for (var j = 0; j < PurchasedData.results.length; j++) {
        PurchasedData.results[j].user.rewardid = PurchasedData.results[j].id;
        if (PurchasedData.results[j].status == 'pending') {
          rejectreward_purchased.push(PurchasedData[j]);
        } else if (PurchasedData[j].status == 'pending') {
          acceptreward_purchased.push(PurchasedData[j]);
        }
      }
    }

    setacceptearnData(acceptreward_earned);
    setrejectearnData(rejectreward_earned);
    setacceptpurchaseData(acceptreward_purchased);
    setrejectpurchaseData(rejectreward_purchased);

    setRewardData(RewardedData);
    setPurchaseData(PurchasedData.results);
  };

  const tablerewardcolumns = useMemo(
    () => [
      {
        Header: t('approve.user'),
        accessor: 'user',
        Cell: ({value}) => (
          <div className="flex items-center">
            <div>
              {value.profile && value.profile.profile_image !== null ? (
                <img
                  src={value.profile.profile_image}
                  className="user-avatar"
                  alt="User avatar"
                />
              ) : (
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1077/1077063.png"
                  className="user-avatar"
                  alt="User avatar"
                />
              )}
            </div>
            <div className="px-4">
              <p className="text-left">
                {value.first_name} {value.last_name}
              </p>
            </div>
          </div>
        ),
      },
      {
        Header: t('approve.received'),
        accessor: 'points',
        Cell: ({value}) => (
          <div>
            + {value} {t('approve.points')}
          </div>
        ),
      },
      {
        Header: t('approve.detail'),
        id: 'reward_detail',
        Cell: ({row}) => (
          <div>{formatRewardApprovalDetail(row.original)}</div>
        ),
      },
      {
        Header: t('approve.date'),
        accessor: 'created_at',
        Cell: ({value}) => (
          <div>{formatLocalizedDate(value, i18n.language)}</div>
        ),
      },
      {
        Header: t('approve.status'),
        accessor: 'accepted',
        Cell: ({row}) => (
          <div className="inline-flex items-center">
            <>
              <Button
                onClick={() => {
                  acceptEarn(row);
                }}
                label={t('approve.accept')}
                type="primary"
              />
              <Button
                onClick={() => {
                  rejectEarn(row);
                }}
                label={t('approve.reject')}
                type="secondary"
              />
            </>
          </div>
        ),
      },
    ],
    [t, i18n.language],
  );

  const tablegiftcolumns = useMemo(
    () => [
      {
        Header: t('approve.user'),
        accessor: 'user',
        Cell: ({value}) => (
          <div className="flex items-center">
            <div>
              {value.profile && value.profile.profile_image !== null ? (
                <img
                  src={value.profile.profile_image}
                  className="user-avatar"
                  alt="User avatar"
                />
              ) : (
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1077/1077063.png"
                  className="user-avatar"
                  alt="User avatar"
                />
              )}
            </div>
            <div className="px-4">
              <p className="text-left">
                {value.first_name} {value.last_name}
              </p>
            </div>
          </div>
        ),
      },
      {
        Header: t('approve.level'),
        accessor: 'level',
        Cell: ({value}) => <div className="text-uppercase">{value}</div>,
      },
      {
        Header: t('approve.detail'),
        id: 'gift_detail',
        Cell: ({row}) => (
          <div>{formatGiftApprovalDetail(row.original)}</div>
        ),
      },
      {
        Header: t('approve.date'),
        accessor: 'created_at',
        Cell: ({value}) => (
          <div>{formatLocalizedDate(value, i18n.language)}</div>
        ),
      },
      {
        Header: t('approve.status'),
        accessor: 'accepted',
        Cell: ({row}) => (
          <div className="inline-flex items-center">
            <>
              <Button
                onClick={() => {
                  acceptGift(row);
                }}
                label={t('approve.accept')}
                type="primary"
              />
              <Button
                onClick={() => {
                  rejectGift(row);
                }}
                label={t('approve.reject')}
                type="secondary"
              />
            </>
          </div>
        ),
      },
    ],
    [t, i18n.language],
  );

  const tablepurchasecolumns = useMemo(
    () => [
      {
        Header: t('approve.user'),
        accessor: 'user',
        Cell: ({value}) => (
          <div className="flex items-center">
            <div>
              {value.profile && value.profile.profile_image !== null ? (
                <img
                  src={value.profile.profile_image}
                  className="user-avatar"
                  alt="User avatar"
                />
              ) : (
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1077/1077063.png"
                  className="user-avatar"
                  alt="User avatar"
                />
              )}
            </div>
            <div className="px-4">
              <p className="text-left">
                {value.first_name} {value.last_name}
              </p>
            </div>
          </div>
        ),
      },
      {
        Header: t('approve.confirmation'),
        accessor: 'product_id',
      },
      {
        Header: t('approve.paid'),
        accessor: 'points_paid',
        Cell: ({value}) => (
          <div>
            - {value} {t('approve.points')}
          </div>
        ),
      },
      {
        Header: t('approve.purchased'),
        accessor: 'product_name',
      },
      {
        Header: 'Status',
        accessor: 'accepted',
        Cell: ({row}) => (
          <div className="inline-flex items-center">
            <>
              <Button
                onClick={() => acceptPurchase(row)}
                label={t('approve.accept')}
                type="primary"
                disabled={processingPurchases[row.original.id]}
                loading={processingPurchases[row.original.id]}
              />
              <Button
                onClick={() => rejectPurchase(row)}
                label={t('approve.reject')}
                type="secondary"
                disabled={processingPurchases[row.original.id]}
              />
            </>
          </div>
        ),
      },
    ],
    [t, processingPurchases],
  );

  const handleApproveSelected = (event, id, data, action = 'single') => {
    console.log(id);
    let temp = [...selectedRow];
    if (event.target.checked) {
      if (action === 'all') {
        temp = data.map(item => item.id);
      } else {
        if (!temp.includes(id)) {
          temp.push(id);
        }
      }
    } else {
      if (action === 'all') {
        temp = [];
      } else {
        let index = temp.indexOf(id);
        temp.splice(index, 1);
      }
    }
    setSelectedRow(temp);
  };

  return (
    <ContentWrapper
      hasSearch={false}
      breadcrumb={[
        {
          title: t('sidebar.menuItems.approveRedeems'),
          link: '/approve-redeems',
        },
      ]}>
      <div className="flex flex-col w-full">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            gap: '16px',
            flexWrap: 'wrap',
          }}>
          <h5 className="mb-0">{t('approve.title')}</h5>
          <div
            className="relative"
            style={{minWidth: '240px', maxWidth: '320px', flex: '1'}}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('approve.searchCustomer')}
              className={`border border-gray-200 focus:outline-none focus:border-indigo-700 rounded w-full text-sm text-gray-700 bg-gray-100 pl-10 py-2 ${
                searchQuery ? 'pr-9' : 'pr-3'
              }`}
            />
            <div className="text-gray-500 absolute left-3 top-0 bottom-0 flex items-center pointer-events-none">
              <SearchIcon height={16} />
            </div>
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-0 bottom-0 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={t('approve.clearSearch')}>
                <X size={16} />
              </button>
            ) : null}
          </div>
        </div>
        <div
          style={{
            height: '45px',
            borderRadius: '10px',
            width: '100%',
            display: 'flex',
            textAlign: 'center',
            justifyContent: 'space-between',
          }}>
          <div style={{display: 'flex'}}>
            <div
              className={
                columns === '1'
                  ? 'bg-tableHeaderBg p-2 cursor-pointer'
                  : 'p-2 cursor-pointer'
              }
              style={{borderRadius: '10px 10px 0px 0px'}}
              onClick={() => getuseData('1')}>
              {t('approve.rewards')} ({rewardCount})
            </div>
            <div
              className={
                columns === '2'
                  ? 'bg-tableHeaderBg p-2 cursor-pointer'
                  : 'p-2 cursor-pointer'
              }
              style={{borderRadius: '10px 10px 0px 0px'}}
              onClick={() => getuseData('2')}>
              {t('approve.gifts')} ({giftCount})
            </div>
            <div
              className={
                columns === '3'
                  ? 'bg-tableHeaderBg p-2 ml-1 cursor-pointer'
                  : 'p-2 ml-1 cursor-pointer'
              }
              style={{borderRadius: '10px 10px 0px 0px'}}
              onClick={() => getuseData('3')}>
              {t('approve.purchase')} ({purchaseCount})
            </div>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            {selectedRow && selectedRow.length > 0 && (
              <div
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#666',
                }}>
                {selectedRow.length}{' '}
                {t('approve.itemsSelected', 'item(s) selected')}
              </div>
            )}
            <Button
              type="primary"
              onClick={Acceptslected}
              label={t('approve.acceptSelected')}
              disabled={!selectedRow || selectedRow.length === 0}
              style={{
                opacity: !selectedRow || selectedRow.length === 0 ? 0.5 : 1,
                cursor:
                  !selectedRow || selectedRow.length === 0
                    ? 'not-allowed'
                    : 'pointer',
                marginRight: '8px',
              }}
            />
            <Button
              type="secondary"
              onClick={Rejectslected}
              label={t('approve.rejectSelected')}
              disabled={!selectedRow || selectedRow.length === 0}
              style={{
                opacity: !selectedRow || selectedRow.length === 0 ? 0.5 : 1,
                cursor:
                  !selectedRow || selectedRow.length === 0
                    ? 'not-allowed'
                    : 'pointer',
              }}
            />
          </div>
        </div>

        {columns == '1' ? (
          <>
            <Table
              columns={tablerewardcolumns}
              data={RewardData}
              onSelectedRowChange={handleApproveSelected}
              loading={loading}
              selectedRow={selectedRow}
            />
            {RewardData && RewardData.length === 0 && !loading ? (
              <div>
                <p className="text-center mt-5">{t('approve.noData')}</p>
              </div>
            ) : (
              <div>
                <div className="bg-tableHeaderBg py-2 md:flex md:justify-center gap-x-10 px-4">
                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      rewardpreviousPage !== null
                        ? 'cursor-pointer'
                        : 'pointer-none'
                    }`}
                    onClick={() => {
                      setCurrentPage(currentPage - 1);
                    }}>
                    {t('approve.showPrevious')}
                  </button>

                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      rewardNextPage !== null
                        ? 'cursor-pointer'
                        : 'pointer-none'
                    }`}
                    onClick={() => setCurrentPage(currentPage + 1)}>
                    {t('approve.showNext')}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : columns == '2' ? (
          <>
            <Table
              columns={tablegiftcolumns}
              data={GiftData}
              onSelectedRowChange={handleApproveSelected}
              selectedRow={selectedRow}
              loading={loading}
            />
            {GiftData && GiftData.length === 0 && !loading ? (
              <div>
                <p className="text-center mt-5">{t('approve.noData')}</p>
              </div>
            ) : (
              <div>
                <div className="bg-tableHeaderBg py-2 md:flex md:justify-center gap-x-10 px-4">
                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      giftpreviousPage !== null
                        ? 'cursor-pointer'
                        : 'pointer-none'
                    }`}
                    onClick={() => {
                      setCurrentPage(currentPage - 1);
                    }}>
                    {t('approve.showPrevious')}
                  </button>

                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      giftNextPage !== null ? 'cursor-pointer' : 'pointer-none'
                    }`}
                    onClick={() => setCurrentPage(currentPage + 1)}>
                    {t('approve.showNext')}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <Table
              columns={tablepurchasecolumns}
              data={PurchaseData}
              onSelectedRowChange={handleApproveSelected}
              selectedRow={selectedRow}
              loading={loading}
            />
            {PurchaseData && PurchaseData.length === 0 && !loading ? (
              <div>
                <p className="text-center mt-5">{t('approve.noData')}</p>
              </div>
            ) : (
              <div>
                <div className="bg-tableHeaderBg py-2 md:flex md:justify-center gap-x-10 px-4">
                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      purchasepreviousPage !== null
                        ? 'cursor-pointer'
                        : 'pointer-none'
                    }`}
                    onClick={() => {
                      setCurrentPage(currentPage - 1);
                    }}>
                    {t('approve.showPrevious')}
                  </button>

                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      purchaseNextPage !== null
                        ? 'cursor-pointer'
                        : 'pointer-none'
                    }`}
                    onClick={() => setCurrentPage(currentPage + 1)}>
                    {t('approve.showNext')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ContentWrapper>
  );
};

export default Approve;
